import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import config from '../../config/config';
import DashboardLayout from '../shared/layout/DashboardLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmailReminderBanner from '../../components/common/EmailReminderBanner';
import { Globe, CheckCircle2, Clock, Database, TrendingUp, ArrowRight, BookOpen, FileText, Zap, AlertCircle } from 'lucide-react';
import { getPublikatorMenuItems } from '../../utils/menuItems';
import '../../styles/App.css';
import '../../styles/RekapDataDetails.css';
import '../../styles/PublikatorDashboard.css';

const PublikatorPage = () => {
  const [username, setUsername] = useState('');
  const [userEmail, setUserEmail] = useState(null);
  const [stats, setStats] = useState({
    totalData: 0,
    dataPublished: 0,
    dataPending: 0,
    totalIGT: 0,
    siapPublikasi: 0,
    sudahTerintegrasi: 0
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch user profile untuk cek email
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setUserEmail('');
          return;
        }
        
        const response = await axios.get(`${config.API_URL}/profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        setUserEmail(response.data.email || '');
      } catch (err) {
        console.error('Gagal mengambil data profil:', err);
        setUserEmail('');
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const decoded = jwtDecode(token);

        if (!token || !decoded.twoFactorVerified) {
          navigate('/');
          return;
        }

        if (decoded.role !== 3) {
          navigate('/');
          return;
        }

        setUsername(decoded.username);

        // Fetch stats untuk publikator
        const response = await axios.get(`${config.API_URL}/publikator/stats`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        const siapPublikasi = response.data.dataSiapPublikasi || 0;
        const sudahTerintegrasi = response.data.dataSudahPublikasi || 0;

        setStats({
          ...response.data,
          siapPublikasi,
          sudahTerintegrasi
        });
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const menuItems = getPublikatorMenuItems();

  return (
    <DashboardLayout menuItems={menuItems} userRole="Publikator">
      {/* Email Reminder Banner */}
      <EmailReminderBanner userEmail={userEmail} profilePath="/publikator/profile" />
      
      <div className="publikator-container">
        <div className="rekap-header-card">
          <div className="header-content">
            <h1 style={{ fontSize: '36px', fontWeight: '700', margin: '0 0 10px 0', color: 'white' }}>
              Dashboard Publikasi Data
            </h1>
            <p style={{ fontSize: '16px', opacity: '0.95', margin: '0', color: 'white' }}>
              Kelola dan publikasikan data IGT ke portal publik
            </p>
            <p style={{ fontSize: '14px', opacity: '0.85', margin: '8px 0 0 0', color: 'white' }}>
              Selamat datang, <strong>{username}</strong>!
            </p>
          </div>
        </div>

        <div className="publikator-stats-grid">
          <div className="stat-card stat-card-success" onClick={() => handleNavigate('/publikator/data?filter=sudah')}>
            <div className="stat-card-header">
              <div>
                <p className="stat-card-title">Sudah Dipublikasi</p>
                <h2 className="stat-card-value">{stats.dataPublished}</h2>
              </div>
              <div className="stat-card-icon">
                <Globe size={24} />
              </div>
            </div>
            <p className="stat-card-desc">Data terpublikasi di portal</p>
          </div>

          <div className="stat-card stat-card-warning" onClick={() => handleNavigate('/publikator/data?filter=belum')}>
            <div className="stat-card-header">
              <div>
                <p className="stat-card-title">Belum Dipublikasi</p>
                <h2 className="stat-card-value">{stats.dataPending}</h2>
              </div>
              <div className="stat-card-icon">
                <AlertCircle size={24} />
              </div>
            </div>
            <p className="stat-card-desc">Menunggu untuk dipublikasi</p>
          </div>

          <div className="stat-card stat-card-warning" onClick={() => handleNavigate('/publikator/data?filter=siap')}>
            <div className="stat-card-header">
              <div>
                <p className="stat-card-title">Siap Publikasi</p>
                <h2 className="stat-card-value">{stats.siapPublikasi}</h2>
              </div>
              <div className="stat-card-icon">
                <CheckCircle2 size={24} />
              </div>
            </div>
            <p className="stat-card-desc">Data siap untuk dipublikasi</p>
          </div>
        </div>

        <div className="publikator-info-section">
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon">
                <BookOpen size={20} />
              </div>
              <h3 className="info-card-title">Tugas Publikator</h3>
            </div>
            <ul className="info-card-list">
              <li className="info-card-item">
                <span className="info-card-check">✓</span>
                <span>Publikasikan data IGT yang telah terintegrasi</span>
              </li>
              <li className="info-card-item">
                <span className="info-card-check">✓</span>
                <span>Ubah status publikasi data ke portal publik</span>
              </li>
              <li className="info-card-item">
                <span className="info-card-check">✓</span>
                <span>Kelola visibilitas data di halaman publik</span>
              </li>
            </ul>
          </div>

          <div className="cta-card">
            <div>
              <h3 className="cta-card-title">Mulai Publikasi Data</h3>
              <p className="cta-card-desc">Kelola dan publikasikan data IGT Anda sekarang. Ubah status publikasi dengan mudah dan pantau progress publikasi secara real-time.</p>
            </div>
            <button className="cta-card-button" onClick={() => handleNavigate('/publikator/data')}>
              <Globe size={16} />
              Kelola Publikasi Data
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay-fullscreen">
          <LoadingSpinner message="Memuat dashboard..." type="spinner" size="large" />
        </div>
      )}
    </DashboardLayout>
  );
};

export default PublikatorPage;